import { useState, useEffect, useCallback } from 'react';
import { useModal, ButtonColor, ButtonVariant } from '@ampol/design-system';
import { useDispatch } from 'react-redux';
import { setCardDefaultButton } from 'actions/modalMetaActions';
import {
  getCardDetailRequest,
  hideCardCreatedModal,
  updateCardModalFormValues,
  getCardsRequest,
  globalSearchReset,
  updateSelectedCard,
} from 'actions/cardActions';
import { trackCustomEvent } from 'utilities/app-insights';
import Env from 'Env';
import Label from 'constants/labels';
import CardDetailModal from '../../../modals/card-detail-modal/card-detail-modal';

/**
 * Custom hook for managing all modal logic in CardsList component.
 *
 * Consolidates:
 * - Card detail modal (with complex actions, GTM tracking, cleanup)
 * - Card creation success modal (with clipboard copy, refresh)
 * - Add/Edit card form modal
 * - Modal event handlers (add, edit, close)
 *
 * @remarks
 * Extracted from CardsList component to reduce complexity.
 *
 * @param selectedCard - Currently selected card object
 * @param setSelectedCard - Function to update selected card state
 * @param modalType - Type of modal context (account or individual)
 * @param cardDetail - Full card detail data from store
 * @param cardDetailLoading - Loading state for card detail
 * @param primaryActionButton - Primary action configuration from modal store
 * @param secondaryActionButton - Secondary action configuration from modal store
 * @param isPreviousOrReplacementCardClicked - Flag for card navigation state
 * @param previousOrReplacementCardNumber - Previous/replacement card number
 * @param createdCardNumber - Newly created card number
 * @param showCreatedCardModal - Flag to show creation success modal
 * @param isGlobalSearchLoaded - Flag indicating if global search is active
 * @param btnInActivate - Flag indicating if activate button should be disabled
 * @param handleActivateCardClick - Callback for card activation
 * @param newAddCardModal - New add card modal instance
 * @param newEditCardModal - New edit card modal instance
 * @param loggedInUser - Current logged-in user object
 * @param assignedAccountNumber - User's assigned account number
 * @param assignedGrandfatherClientNumber - User's grandfather client number
 */
export const useCardsListModals = ({
  selectedCard,
  setSelectedCard,
  modalType,
  cardDetail,
  cardDetailLoading,
  primaryActionButton,
  secondaryActionButton,
  isPreviousOrReplacementCardClicked,
  previousOrReplacementCardNumber,
  createdCardNumber,
  showCreatedCardModal,
  isGlobalSearchLoaded,
  btnInActivate,
  handleActivateCardClick,
  newAddCardModal,
  newEditCardModal,
  loggedInUser,
  assignedAccountNumber,
  assignedGrandfatherClientNumber,
}: {
  selectedCard: any;
  setSelectedCard: (card: any) => void;
  modalType?: string;
  cardDetail: any;
  cardDetailLoading: boolean;
  primaryActionButton: any;
  secondaryActionButton: any;
  isPreviousOrReplacementCardClicked: boolean;
  previousOrReplacementCardNumber: string;
  createdCardNumber: string;
  showCreatedCardModal: boolean;
  isGlobalSearchLoaded: boolean;
  btnInActivate: boolean;
  handleActivateCardClick: () => void;
  newAddCardModal: any;
  newEditCardModal: any;
  loggedInUser: any;
  assignedAccountNumber: string;
  assignedGrandfatherClientNumber: string;
}) => {
  const dispatch = useDispatch();
  const [showAddEditCard, setShowAddEditCard] = useState(false);

  // STABILITY FIX: Memoize callback to prevent recreation on every render
  // This callback is passed to useCardsListTabs which uses it in dependencies
  const handleItemSelected = useCallback(
    (item: any) => {
      setSelectedCard(item);
      dispatch(updateSelectedCard(item));
      trackCustomEvent(
        'Open Card Detail Modal',
        loggedInUser,
        assignedAccountNumber,
        'Clicked on card row to view card details.',
        { selectedAccount: item?.parent, selectedCard: item?.cardNumber }
      );
    },
    [setSelectedCard, dispatch, loggedInUser, assignedAccountNumber]
  );

  const handleAddCardClick = () => {
    trackCustomEvent(
      'Click Add New Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked on Add New Card button to open Add New Card modal.'
    );
    if (Env.getIsNewCardModalsEnabled() === 'true') {
      window.dataLayer.push({
        event: 'Card_Portal_Add_New_Card_Button_Start',
        button_click: true,
      });

      newAddCardModal.open();
    } else setShowAddEditCard(true);
  };

  const handleEditCardClick = () => {
    if (modalType && modalType === 'account') {
      window.dataLayer.push({
        event: 'Card_Portal_Accounts_Card_Detail_Edit',
        button_click: true,
      });
    } else {
      window.dataLayer.push({
        event: 'Card_Portal_Individual_Card_Detail_Edit',
        button_click: true,
      });
    }
    trackCustomEvent(
      'Click Edit Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked on Edit card button to open edit card modal.',
      { selectedAccount: selectedCard?.parent, selectedCard: selectedCard?.cardNumber }
    );
    if (Env.getIsNewCardModalsEnabled() === 'true') newEditCardModal.open();
    else setShowAddEditCard(true);
  };

  const handleClose = () => {
    detailModal.close();
  };

  const getModalActions = () => [
    ...(primaryActionButton ? [primaryActionButton] : []),
    ...(secondaryActionButton
      ? [{ ...secondaryActionButton, onClick: secondaryActionButton.onClick || handleClose }]
      : []),
    ...(isPreviousOrReplacementCardClicked
      ? [
          {
            label: 'Back',
            variant: ButtonVariant.Tertiary,
            onClick: () =>
              dispatch(
                getCardDetailRequest(
                  selectedCard.cardNumber,
                  assignedGrandfatherClientNumber,
                  selectedCard.cardNumber !== previousOrReplacementCardNumber
                )
              ),
          },
        ]
      : []),
  ];

  const detailModal = useModal({
    id: 'card-details',
    title: `${cardDetailLoading ? Label.LOADING : cardDetail?.contractName ? cardDetail.contractName : 'Unknown'}`,
    subtitle: 'Account Name',
    content: <CardDetailModal selectedCard={selectedCard} onEditCardClick={handleEditCardClick} />,
    variant: 'triple',
    color: ButtonColor.Blue,
    actions: getModalActions(),
    onClose: () => {
      if (modalType && modalType === 'account') {
        window.dataLayer.push({
          event: 'Card_Portal_Accounts_Card_Detail_Close',
          button_click: true,
        });
      } else {
        window.dataLayer.push({
          event: 'Card_Portal_Individual_Card_Detail_Close',
          button_click: true,
        });
      }
      setSelectedCard(null);
      dispatch(updateCardModalFormValues({}));
      trackCustomEvent('Close Card Modal', loggedInUser, assignedAccountNumber, 'Closed Card Detail modal.', {
        selectedAccount: selectedCard?.parent,
        selectedCard: selectedCard?.cardNumber,
      });
      if (isGlobalSearchLoaded) {
        dispatch(globalSearchReset());
      }
    },
    formId: primaryActionButton?.formId,
    topActions:
      selectedCard?.status === 'Inactive'
        ? [
            {
              label: 'Activate',
              onClick: () => (btnInActivate ? undefined : handleActivateCardClick()),
              inactive: btnInActivate,
            },
          ]
        : undefined,
  });

  const successModal = useModal({
    color: ButtonColor.Blue,
    title: 'New card added',
    id: 'add-card-creation-id',
    variant: 'double',
    content: <p>{`A new card with number ${createdCardNumber} has been successfully added.`}</p>,
    actions: [
      {
        label: 'OK',
        onClick: () => {
          successModal.close();
          const payload = {
            contractNumber: assignedAccountNumber,
            parent: assignedGrandfatherClientNumber,
            startRange: '',
            endRange: '',
            accountNumber: '',
            status: 'All',
            issueDateFrom: '',
            issueDateTo: '',
            driverName: '',
            vehicleRegNumber: '',
            domesticNumber: '',
            costCentre: '',
            page: 1,
            pageSize: 10,
            sortColumn: 'CardNumber',
            sortDirection: false,
          };
          dispatch(getCardsRequest(payload, true));
        },
      },
      {
        label: 'COPY TO CLIPBOARD',
        onClick: () => {
          navigator.clipboard.writeText(createdCardNumber);
        },
      },
    ],
  });

  // Open detail modal when card is selected
  // Legitimate effect: External system sync (modal library, Redux API calls)
  useEffect(() => {
    if (selectedCard) {
      dispatch(setCardDefaultButton());
      dispatch(getCardDetailRequest(selectedCard.cardNumber, assignedGrandfatherClientNumber));
      detailModal.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard, assignedGrandfatherClientNumber]);
  // Note: detailModal and dispatch are stable, omitted to avoid recreation cycles

  // Open success modal when card is created
  // Legitimate effect: External system sync (modal library, Redux dispatch, GTM tracking)
  useEffect(() => {
    if (showCreatedCardModal && !!createdCardNumber) {
      window.dataLayer.push({
        event: 'Card_Portal_Add_New_Card_Submit_Pass',
        button_click: true,
      });
      successModal.open();
      dispatch(hideCardCreatedModal());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreatedCardModal, createdCardNumber]);
  // Note: successModal and dispatch are stable, omitted to avoid recreation cycles

  return {
    detailModal,
    successModal,
    showAddEditCard,
    setShowAddEditCard,
    handleAddCardClick,
    handleEditCardClick,
    handleItemSelected,
  };
};
